import React, { useCallback, useEffect, useMemo, useState, Suspense, lazy } from "react";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import "@/App.css";

import Header from "@/components/Header";
// All of the heavy Dashboard widgets below are needed ONLY when the user
// visits /futures · /dashboard. They each pull Recharts and other chunky
// libs, so lazy-loading them slims the initial bundle further. The
// route-level <Suspense> in AppRouter handles their loading states.
const KCEPromoBanner       = lazy(() => import(/* webpackChunkName: "dashboard" */ "@/components/KCEPromoBanner"));
const GEXChart             = lazy(() => import(/* webpackChunkName: "dashboard" */ "@/components/GEXChart"));
const MarketBrief          = lazy(() => import(/* webpackChunkName: "dashboard" */ "@/components/MarketBrief"));
const StrategiesGrid       = lazy(() => import(/* webpackChunkName: "dashboard" */ "@/components/StrategiesGrid"));
const StrategyLibrary      = lazy(() => import(/* webpackChunkName: "dashboard" */ "@/components/StrategyLibrary"));
const StrategyLeaderboard  = lazy(() => import(/* webpackChunkName: "dashboard" */ "@/components/StrategyLeaderboard"));
const GEXEducation         = lazy(() => import(/* webpackChunkName: "dashboard" */ "@/components/GEXEducation"));
const IVCard               = lazy(() => import(/* webpackChunkName: "dashboard" */ "@/components/IVCard"));
const Recommendations      = lazy(() => import(/* webpackChunkName: "dashboard" */ "@/components/Recommendations"));
const Watchlist            = lazy(() => import(/* webpackChunkName: "dashboard" */ "@/components/Watchlist"));
const TradingPrimer        = lazy(() => import(/* webpackChunkName: "dashboard" */ "@/components/TradingPrimer"));
const EmailCaptureBanner   = lazy(() => import(/* webpackChunkName: "dashboard" */ "@/components/EmailCaptureBanner"));
const FuturesChartPanel    = lazy(() => import(/* webpackChunkName: "dashboard" */ "@/components/FuturesChartPanel"));
import AuthCallback from "@/components/AuthCallback";
import InstallPWAPrompt from "@/components/InstallPWAPrompt";

// Route-level lazy chunks — these were eagerly bundled and added 60%+ to the
// initial JS payload even when the user only visited "/". Lazy imports let
// each page download on demand and dramatically improve first-paint TTI.
// Webpack /* webpackPrefetch */ hints emit <link rel="prefetch"> so the
// browser pre-loads these bundles during idle time on the landing page —
// next navigation feels instant.
const LandingPage     = lazy(() => import("@/components/LandingPage"));
const EquitiesPage    = lazy(() => import("@/components/EquitiesPage"));
const SmartMoneyPage  = lazy(() => import("@/components/SmartMoneyPage"));
const SmartMoneyRecallPage = lazy(() => import("@/components/SmartMoneyRecallPage"));
const RecallSharePage = lazy(() => import("@/components/RecallSharePage"));
const WorkspacePage = lazy(() => import("@/components/WorkspacePage"));
const SignalsPage     = lazy(() => import("@/components/SignalsPage"));
const KCEGuidePage    = lazy(() => import("@/components/KCEGuidePage"));
const AccountPage     = lazy(() => import("@/components/AccountPage"));
const LearnPage       = lazy(() => import("@/components/LearnPage"));
const AnalysisPage    = lazy(() => import(/* webpackPrefetch: true */ "@/components/AnalysisPage"));
const AdminPage       = lazy(() => import("@/components/AdminPage"));
const ForgePage       = lazy(() => import(/* webpackPrefetch: true */ "@/components/ForgePage"));
// Collar is now embedded inside Markets Pro as a tab; /collar redirects there.
const OptionsVisualizerPreview = lazy(() => import(/* webpackChunkName: "forge-preview" */ "@/components/OptionsVisualizerPreview"));
const ForgeSharePage  = lazy(() => import("@/components/ForgeSharePage"));
const AcademyPage     = lazy(() => import("@/components/AcademyPage"));
const VaultPage       = lazy(() => import("@/components/VaultPage"));
const MarketsProPage  = lazy(() => import("@/components/MarketsProPage"));
const MacroSignalsPage = lazy(() => import("@/components/MacroSignalsPage"));
// Legal pages (Privacy / Terms / Security / Data Retention / Vulnerability Disclosure)
const PrivacyPolicy           = lazy(() => import("@/components/legal/PrivacyPolicy"));
const TermsOfService          = lazy(() => import("@/components/legal/TermsOfService"));
const SecurityPolicy          = lazy(() => import("@/components/legal/SecurityPolicy"));
const DataRetention           = lazy(() => import("@/components/legal/DataRetention"));
const VulnerabilityDisclosure = lazy(() => import("@/components/legal/VulnerabilityDisclosure"));
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { useGlobalClickHaptic } from "@/lib/haptic";
import { useKCESignalsPoll } from "@/hooks/useKCESignalsPoll";
import {
  fetchQuotes,
  fetchGEX,
  fetchStrategies,
  fetchBrief,
  regenerateBrief,
  fetchIV,
  fetchRecommendations,
  fetchBacktest,
  getSavedRecos,
  SYMBOLS,
} from "@/lib/api";
import { Toaster, toast } from "sonner";
import { ArrowUpRight, Cpu, LineChart, Zap } from "lucide-react";
import { useBillingReturnHandler } from "@/components/ProUpgrade";

const SymbolSwitcher = ({ active, onChange, quotes }) => (
  <div
    className="flex items-center gap-1 p-1 bg-[#0a0a0c] border border-white/10 rounded-lg overflow-x-auto no-scrollbar"
    data-testid="symbol-switcher"
    role="tablist"
  >
    {SYMBOLS.map((s) => {
      const q = quotes.find((x) => x.symbol === s.key);
      const isActive = active === s.key;
      const positive = (q?.change || 0) >= 0;
      return (
        <button
          key={s.key}
          type="button"
          role="tab"
          aria-selected={isActive}
          onClick={() => onChange(s.key)}
          data-testid={`symbol-${s.key.replace("/", "")}`}
          className={[
            "shrink-0 px-2 sm:px-3 py-1.5 rounded-md text-[12px] font-medium transition-all flex items-center gap-1 sm:gap-2",
            isActive
              ? "bg-white text-black shadow-sm"
              : "text-white/70 hover:text-white hover:bg-white/5",
          ].join(" ")}
        >
          <span>{s.key}</span>
          {q && (
            <span
              className={`num text-[9px] sm:text-[10px] ${
                isActive
                  ? positive ? "text-[#007a04]" : "text-[#a01a12]"
                  : positive ? "text-[#00C805]" : "text-[#FF3B30]"
              }`}
            >
              {positive ? "+" : ""}{q.change_percent.toFixed(2)}%
            </span>
          )}
        </button>
      );
    })}
  </div>
);

const StatPill = ({ label, value, color = "#fff", testid }) => (
  <div className="flex flex-col gap-0.5" data-testid={testid}>
    <div className="text-[10px] uppercase tracking-[0.22em] text-white/40">{label}</div>
    <div className="num text-base sm:text-lg font-medium" style={{ color }}>
      {value}
    </div>
  </div>
);

const LS_KEY = "gamma_terminal_active_symbol";
const QUOTE_REFRESH_MS = 30 * 1000;

const Dashboard = () => {
  const [quotes, setQuotes] = useState([]);
  const [quotesLoading, setQuotesLoading] = useState(true);
  const [activeSymbol, setActiveSymbol] = useState(() => {
    try {
      const saved = localStorage.getItem(LS_KEY);
      if (saved && SYMBOLS.some((s) => s.key === saved)) return saved;
    } catch (_e) { /* ignore */ }
    return "SPX";
  });
  const [gex, setGex] = useState(null);
  const [gexLoading, setGexLoading] = useState(true);
  const [strategies, setStrategies] = useState([]);
  const [stratsLoading, setStratsLoading] = useState(true);
  const [backtests, setBacktests] = useState([]);
  const [iv, setIv] = useState(null);
  const [ivLoading, setIvLoading] = useState(true);
  const [brief, setBrief] = useState(null);
  const [briefLoading, setBriefLoading] = useState(true);
  const [briefRefreshing, setBriefRefreshing] = useState(false);
  const [recos, setRecos] = useState([]);
  const [recosLoading, setRecosLoading] = useState(true);
  const [lastTick, setLastTick] = useState(null);
  const [savedSet, setSavedSet] = useState(new Set());
  const { user } = useAuth();
  useBillingReturnHandler();

  // Persist active symbol
  useEffect(() => {
    try { localStorage.setItem(LS_KEY, activeSymbol); } catch (_e) { /* ignore */ }
  }, [activeSymbol]);

  // Initial load — fetch quotes/brief/recos in parallel so the dashboard
  // paints faster. Previously these awaited sequentially which serialized
  // ~3 cold round-trips (quotes 200ms + brief 1-2s + recos 300ms) ≈ 2.5s,
  // versus ~max(latencies) ≈ 1.5s with allSettled.
  useEffect(() => {
    let mounted = true;
    Promise.allSettled([fetchQuotes(), fetchBrief(), fetchRecommendations()])
      .then(([qRes, bRes, rRes]) => {
        if (!mounted) return;
        if (qRes.status === "fulfilled") {
          setQuotes(qRes.value);
          setLastTick(new Date());
        } else {
          console.error("quotes failed", qRes.reason);
        }
        setQuotesLoading(false);
        if (bRes.status === "fulfilled") setBrief(bRes.value);
        else console.error("brief failed", bRes.reason);
        setBriefLoading(false);
        if (rRes.status === "fulfilled") setRecos(rRes.value);
        else console.error("recos failed", rRes.reason);
        setRecosLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, []);

  // Background polling: refresh quotes every 30 seconds
  useEffect(() => {
    const id = setInterval(async () => {
      try {
        const q = await fetchQuotes();
        setQuotes(q);
        setLastTick(new Date());
      } catch (_e) { /* swallow */ }
    }, QUOTE_REFRESH_MS);
    return () => clearInterval(id);
  }, []);

  // Refresh GEX + strategies + IV + backtests whenever activeSymbol changes
  useEffect(() => {
    let mounted = true;
    setGexLoading(true);
    setStratsLoading(true);
    setIvLoading(true);
    (async () => {
      try {
        const [g, s, ivd, bt] = await Promise.all([
          fetchGEX(activeSymbol),
          fetchStrategies(activeSymbol),
          fetchIV(activeSymbol),
          fetchBacktest(activeSymbol).catch(() => []),
        ]);
        if (!mounted) return;
        setGex(g);
        setStrategies(s);
        setIv(ivd);
        setBacktests(bt || []);
      } catch (e) {
        console.error("gex/strategies/iv failed", e);
        if (mounted) {
          setGex(null);
          setStrategies([]);
          setIv(null);
          setBacktests([]);
        }
        toast.error("Failed to load market data");
      } finally {
        if (mounted) {
          setGexLoading(false);
          setStratsLoading(false);
          setIvLoading(false);
        }
      }
    })();
    return () => {
      mounted = false;
    };
  }, [activeSymbol]);

  // Load saved recommendations when user signs in / out
  useEffect(() => {
    let mounted = true;
    if (!user) {
      setSavedSet(new Set());
      return;
    }
    (async () => {
      try {
        const list = await getSavedRecos();
        if (mounted) setSavedSet(new Set(list.map((r) => r.symbol)));
      } catch (_e) { /* ignore */ }
    })();
    return () => { mounted = false; };
  }, [user]);

  const onRecoSaved = useCallback((symbol) => {
    setSavedSet((prev) => new Set(prev).add(symbol));
    setSavedRefreshKey((k) => k + 1);
  }, []);

  const [savedRefreshKey, setSavedRefreshKey] = useState(0);

  const onRefreshBrief = useCallback(async () => {
    setBriefRefreshing(true);
    try {
      const b = await regenerateBrief();
      setBrief(b);
      toast.success("Market brief refreshed");
    } catch (e) {
      toast.error("Could not refresh brief");
    } finally {
      setBriefRefreshing(false);
    }
  }, []);

  const onRefreshRecos = useCallback(async () => {
    setRecosLoading(true);
    try {
      const rs = await fetchRecommendations();
      setRecos(rs);
      toast.success("Recommendations refreshed");
    } catch (e) {
      toast.error("Could not refresh recommendations");
    } finally {
      setRecosLoading(false);
    }
  }, []);

  const activeQuote = quotes.find((q) => q.symbol === activeSymbol);

  const heroStats = useMemo(() => {
    if (!gex) return null;
    return {
      spot: gex.spot,
      flip: gex.gamma_flip,
      call_wall: gex.call_wall,
      put_wall: gex.put_wall,
      net: gex.net_gex,
    };
  }, [gex]);

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <Header />

      {/* Spacer for fixed header */}
      <div className="h-header-with-safe" />

      {/* KCE Pro signals promo */}
      <KCEPromoBanner />

      {/* HERO */}
      <section className="relative overflow-hidden" data-testid="hero-section">
        <div className="absolute inset-0 grid-bg radial-fade opacity-70" />
        <div className="relative max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-10 pt-6 sm:pt-10 lg:pt-14 pb-10">
          <div className="flex flex-col gap-5 max-w-3xl">
            <span className="inline-flex w-fit items-center gap-2 text-[10px] uppercase tracking-[0.28em] text-white/60 border border-white/10 bg-white/[0.03] rounded-full px-3 py-1">
              <Zap className="w-3 h-3 text-[#FFD60A]" />
              Realtime · Options Flow · {new Date().toLocaleString("en-US", { month: "short", year: "numeric" }).toUpperCase()}
            </span>
            <h1 className="text-3xl sm:text-5xl lg:text-6xl tracking-tighter font-medium leading-[1.05]">
              Trade the <span className="text-[#00C805]">gamma surface</span>,<br className="hidden sm:block" />
              not the headlines.
            </h1>
            <p className="text-sm sm:text-base text-white/60 max-w-2xl leading-relaxed">
              Live dealer positioning for SPX, /ES, /NQ, /GC, /MCL, /ZN. Read the call wall, watch
              the gamma flip, and act on AI-curated strategies — all in one terminal.
            </p>
            <div className="flex flex-wrap items-center gap-3">
              <a
                href="#gex"
                data-testid="hero-cta-primary"
                className="inline-flex items-center gap-1.5 bg-white text-black text-sm font-medium px-4 py-2 rounded-md hover:bg-white/90 transition"
              >
                View GEX Chart <ArrowUpRight className="w-3.5 h-3.5" />
              </a>
              <a
                href="#strategies"
                data-testid="hero-cta-secondary"
                className="inline-flex items-center gap-1.5 text-sm text-white/80 hover:text-white border border-white/10 hover:border-white/30 px-4 py-2 rounded-md transition"
              >
                Today's Strategies
              </a>
            </div>
            <div className="flex items-center gap-5 text-[11px] uppercase tracking-[0.22em] text-white/40 mt-3">
              <span className="inline-flex items-center gap-1.5">
                <LineChart className="w-3 h-3" /> Live quotes
              </span>
              <span className="inline-flex items-center gap-1.5">
                <Cpu className="w-3 h-3" /> Claude Sonnet 4.5 brief
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* Dashboard grid */}
      <section
        id="dashboard"
        className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-10 pb-12"
        data-testid="dashboard-section"
      >
        <TradingPrimer />
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 lg:gap-5">
          {/* GEX panel (2/3) */}
          <div
            id="gex"
            className="lg:col-span-2 bg-[#0c0c0e] border border-white/10 rounded-xl p-4 sm:p-6"
            data-testid="gex-panel"
          >
            <div className="flex flex-col gap-4">
              <div className="flex flex-wrap items-end justify-between gap-3">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.22em] text-white/40">Gamma Exposure</div>
                  <h2 className="text-2xl sm:text-3xl tracking-tight font-medium">
                    {activeSymbol}{" "}
                    <span className="text-white/40 text-base font-normal">
                      {SYMBOLS.find((s) => s.key === activeSymbol)?.label}
                    </span>
                  </h2>
                </div>
                <SymbolSwitcher
                  active={activeSymbol}
                  onChange={setActiveSymbol}
                  quotes={quotes}
                />
              </div>

              {/* GEX stats row */}
              {heroStats && (
                <div
                  className="grid grid-cols-2 sm:grid-cols-5 gap-3 sm:gap-4 py-3 border-y border-white/5"
                  data-testid="gex-stats"
                >
                  <StatPill testid="stat-spot" label="Spot" value={`$${heroStats.spot}`} />
                  <StatPill
                    testid="stat-flip"
                    label="Gamma Flip"
                    value={`$${heroStats.flip}`}
                    color="#FFD60A"
                  />
                  <StatPill
                    testid="stat-call-wall"
                    label="Call Wall"
                    value={`$${heroStats.call_wall}`}
                    color="#00C805"
                  />
                  <StatPill
                    testid="stat-put-wall"
                    label="Put Wall"
                    value={`$${heroStats.put_wall}`}
                    color="#FF3B30"
                  />
                  <StatPill
                    testid="stat-net-gex"
                    label="Net GEX"
                    value={`${heroStats.net >= 0 ? "+" : ""}${heroStats.net.toFixed(2)}B`}
                    color={heroStats.net >= 0 ? "#00C805" : "#FF3B30"}
                  />
                </div>
              )}

              <GEXChart data={gex} loading={gexLoading} />

              {activeQuote && (
                <div className="flex flex-wrap items-center gap-x-6 gap-y-1 pt-3 border-t border-white/5 text-[11px] text-white/50">
                  <span>
                    Underlying proxy:{" "}
                    <span className="num text-white/80">{activeQuote.proxy}</span>
                  </span>
                  <span>
                    Range:{" "}
                    <span className="num text-white/80">
                      ${activeQuote.low} – ${activeQuote.high}
                    </span>
                  </span>
                  <span>
                    Volume:{" "}
                    <span className="num text-white/80">
                      {activeQuote.volume.toLocaleString()}
                    </span>
                  </span>
                  <span className="text-white/30">As of {activeQuote.last_updated}</span>
                  {lastTick && (
                    <span className="text-white/30 ml-auto inline-flex items-center gap-1.5" data-testid="last-tick">
                      <span className="w-1.5 h-1.5 rounded-full bg-[#00C805] live-dot" />
                      Auto-refresh · {lastTick.toLocaleTimeString()}
                    </span>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Right column: Brief + IV card stacked */}
          <div className="lg:col-span-1 flex flex-col gap-4">
            <MarketBrief
              brief={brief}
              loading={briefLoading}
              refreshing={briefRefreshing}
              onRefresh={onRefreshBrief}
            />
            <IVCard iv={iv} loading={ivLoading} />
          </div>
        </div>
      </section>

      {/* GEX Education primer */}
      <GEXEducation />

      {/* TradingView + KCE swap-pane chart for the active futures symbol */}
      <FuturesChartPanel symbol={activeSymbol} />

      {/* Strategies */}
      <section
        id="strategies"
        className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-10 pb-12"
        data-testid="strategies-section"
      >
        <header className="flex flex-wrap items-end justify-between gap-3 mb-5">
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-white/40">Live Recommendations</div>
            <h2 className="text-2xl sm:text-3xl tracking-tight font-medium">
              {activeSymbol} Strategies
            </h2>
          </div>
          <span className="text-[11px] uppercase tracking-[0.18em] text-white/50">
            Driven by gamma positioning
          </span>
        </header>
        <StrategiesGrid strategies={strategies} loading={stratsLoading} spot={gex?.spot} backtests={backtests} />
      </section>

      {/* Stock & ETF Recommendations */}
      <Recommendations
        items={recos}
        loading={recosLoading}
        onRefresh={onRefreshRecos}
        savedSet={savedSet}
        onSaved={onRecoSaved}
      />

      {/* Watchlist (auth-gated, renders nothing if not signed in) */}
      <Watchlist user={user} onSwitchSymbol={setActiveSymbol} refreshKey={savedRefreshKey} />

      {/* Email capture banner */}
      <EmailCaptureBanner />

      {/* Strategy Performance Leaderboard */}
      <StrategyLeaderboard />

      {/* Strategy Library (12 strategies, Pro-locked where applicable) */}
      <StrategyLibrary />

      {/* Footer */}
      <footer className="border-t border-white/10 py-6 text-[11px] text-white/40" data-testid="site-footer">
        <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-10 flex flex-wrap items-center justify-between gap-2">
          <span>© {new Date().getFullYear()} Gamma Trade · Informational only — not investment advice.</span>
          <span className="uppercase tracking-[0.22em]">Data: Alpha Vantage · AI: Claude Sonnet 4.5</span>
        </div>
      </footer>
    </div>
  );
};

function AppRouter() {
  const location = useLocation();
  const { user } = useAuth();
  useKCESignalsPoll({ enabled: user?.tier === "pro" });
  useGlobalClickHaptic();
  // Detect Emergent OAuth callback fragment synchronously (before any /auth/me)
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Suspense
      fallback={
        <div className="min-h-screen bg-[#050505] text-white flex items-center justify-center">
          <div className="text-[10px] uppercase tracking-[0.22em] text-white/40 animate-pulse">
            Loading…
          </div>
        </div>
      }
    >
      <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route path="/futures" element={<Dashboard />} />
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/equities" element={<EquitiesPage />} />
        <Route path="/smart-money" element={<SmartMoneyPage />} />
        <Route path="/smart-money/recall" element={<SmartMoneyRecallPage />} />
        <Route path="/recall/share/:shareId" element={<RecallSharePage />} />
        <Route path="/workspace" element={<WorkspacePage />} />
        <Route path="/signals" element={<SignalsPage />} />
        <Route path="/signals/guide" element={<KCEGuidePage />} />
        <Route path="/learn" element={<LearnPage />} />
        <Route path="/analysis" element={<AnalysisPage />} />
        <Route path="/markets" element={<LandingPage />} />
        <Route path="/account" element={<AccountPage />} />
        <Route path="/forge" element={<ForgePage />} />
        <Route path="/collar" element={<Navigate to="/markets-pro?tab=collar" replace />} />
        <Route path="/forge/preview" element={<OptionsVisualizerPreview />} />
        <Route path="/forge/share/:shareId" element={<ForgeSharePage />} />
        <Route path="/academy" element={<AcademyPage />} />
        <Route path="/vault" element={<VaultPage />} />
        <Route path="/markets-pro" element={<MarketsProPage />} />
        <Route path="/smart-money/macro" element={<MacroSignalsPage />} />
        {/* Legal pages — public, no auth required (referenced by Plaid + footer) */}
        <Route path="/legal/privacy" element={<PrivacyPolicy />} />
        <Route path="/legal/terms" element={<TermsOfService />} />
        <Route path="/legal/security" element={<SecurityPolicy />} />
        <Route path="/legal/data-retention" element={<DataRetention />} />
        <Route path="/legal/vulnerability-disclosure" element={<VulnerabilityDisclosure />} />
        <Route path="/admin" element={<AdminPage />} />
      </Routes>
    </Suspense>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <InstallPWAPrompt />
        <AppRouter />
        <Toaster theme="dark" position="bottom-right" />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
